// Product-page "You May Also Like" Algolia Recommend calls.
// The cart watcher does not await getRelated, so a rejection — including
// ApiError "ObjectID does not exist" — must resolve to a list. This module
// takes a Recommend client and index name so it does not import algoliasearch,
// settings, or the Vue component.

const BOUGHT_TOGETHER_BELOW = 12
const BOUGHT_TOGETHER_FILL = 24

function categoryFacetFilters(product) {
	// Same shape the component built: one OR-group of category facets, or none.
	const categoryFilters = product.categories?.length ? [product.categories.map((cat) => `categories:${cat?.name}`)] : []
	return [...categoryFilters]
}

function relatedRequest({ indexName, product, maxRecommendations, threshold }) {
	return {
		indexName,
		model: 'related-products',
		objectID: product.id,
		maxRecommendations,
		threshold,
		queryParameters: {
			filters: 'outOfStock:false',
		},
	}
}

function boughtTogetherRequest({ indexName, product, maxRecommendations, threshold }) {
	return {
		indexName,
		model: 'bought-together',
		objectID: product.id,
		maxRecommendations,
		threshold,
		fallbackParameters: {
			facetFilters: categoryFacetFilters(product),
			filters: 'outOfStock:false',
		},
	}
}

async function recommendationHits(recommend, request) {
	const response = await recommend.getRecommendations({ requests: [request] })
	const hits = response?.results?.[0]?.hits
	return Array.isArray(hits) ? hits : []
}

function reportError(onError, error) {
	if (typeof onError === 'function') onError(error)
}

export async function fetchBoughtTogetherRecommendations({
	recommend,
	indexName,
	product,
	maxRecommendations = 8,
	threshold = 0,
	onError,
} = {}) {
	if (!product?.id) return []
	try {
		return await recommendationHits(recommend, boughtTogetherRequest({ indexName, product, maxRecommendations, threshold }))
	} catch (error) {
		reportError(onError, error)
		return []
	}
}

export async function fetchRelatedRecommendations({ recommend, indexName, product, maxRecommendations = 24, threshold = 0, onError } = {}) {
	if (!product?.id) return []

	let relatedHits
	try {
		relatedHits = await recommendationHits(recommend, relatedRequest({ indexName, product, maxRecommendations, threshold }))
	} catch (error) {
		reportError(onError, error)
		return []
	}

	if (relatedHits.length >= BOUGHT_TOGETHER_BELOW) return relatedHits

	// Remainder is always 24 minus the related count, matching the component
	// call getBoughtTogether(24 - upsellItems.length) which also left threshold at 0.
	const boughtHits = await fetchBoughtTogetherRecommendations({
		recommend,
		indexName,
		product,
		maxRecommendations: BOUGHT_TOGETHER_FILL - relatedHits.length,
		threshold: 0,
		onError,
	})
	return [...relatedHits, ...boughtHits]
}
